/**
 * Блок с конфигами для импорта в разные VPN-клиенты.
 *
 * Рекомендованный клиент — HAPP (https://www.happ.su/).
 * Иранский разработчик специально для Hysteria/Hysteria2, безупречная
 * поддержка subscription URL, в большинстве регионов App Store доступен.
 *
 * Универсальный subscription URL невозможен — разные клиенты ждут разный
 * формат (изучено по докам Karing, Streisand, v2rayTun, HAPP, Marzban,
 * Remnawave). См. memory/feedback_vpn_subscription_formats.md.
 */
import { QRCodeSVG } from "qrcode.react";
import { Tabs, Space, Typography, Alert, Table, Tag, Card, Badge } from "antd";

interface Props {
  subUrl: string;
}

interface ClientFormat {
  key: string;
  label: string;
  url: string;
  description: string;
  clients: string[];
  recommended?: boolean;
}

export const SubUrlBlock = ({ subUrl }: Props) => {
  const formats: ClientFormat[] = [
    {
      key: "happ",
      label: "🌟 HAPP (рекомендуется)",
      url: subUrl,
      description:
        "HAPP — клиент, специально созданный для Hysteria2. Лучшая совместимость, минимум багов, есть в App Store и Google Play. Рекомендуем как первый выбор для всех новых юзеров.",
      clients: ["HAPP iOS", "HAPP Android"],
      recommended: true,
    },
    {
      key: "karing",
      label: "Karing / Hiddify Next",
      url: `${subUrl}?fmt=singbox-full`,
      description:
        "Sing-box JSON с TUN auto-route, DNS-правилами и селекторами. One-click VPN на sing-box-клиентах.",
      clients: ["Karing", "Hiddify Next", "sing-box-NG"],
    },
    {
      key: "v2sub",
      label: "Streisand / v2rayTun / v2rayN",
      url: `${subUrl}?fmt=hy2-list`,
      description:
        "Base64-список Hysteria2 URI — универсальный subscription standard для Xray-клиентов. Best effort: некоторые версии iOS-клиентов имеют свои баги парсера.",
      clients: ["Streisand", "v2rayTun", "Shadowrocket", "NekoBox", "v2rayN", "v2rayNG"],
    },
    {
      key: "direct",
      label: "Прямой hysteria2:// URL",
      url: `${subUrl}?fmt=hy2`,
      description:
        "Single-config URL. Универсальный fallback — копируешь, открываешь в любом Hysteria2-клиенте → Add from clipboard.",
      clients: ["Любой Hysteria2-клиент"],
    },
  ];

  const clientGuide = [
    { client: "HAPP", recommend: "🌟 HAPP", best: true,
      url: "https://www.happ.su/" },
    { client: "Karing", recommend: "Karing / Hiddify Next" },
    { client: "Hiddify Next", recommend: "Karing / Hiddify Next" },
    { client: "Streisand (iOS)", recommend: "Streisand / v2rayTun" },
    { client: "v2rayTun (iOS)", recommend: "Streisand / v2rayTun" },
    { client: "Shadowrocket (iOS)", recommend: "Streisand / v2rayTun" },
    { client: "NekoBox (Android)", recommend: "Streisand / v2rayTun" },
    { client: "v2rayN (Win)", recommend: "Streisand / v2rayTun" },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="success"
        showIcon
        message={<><b>🌟 Рекомендуем HAPP</b> — клиент специально для Hysteria2</>}
        description={
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Typography.Text>
              HAPP создан иранскими разработчиками для протокола Hysteria — лучшая
              совместимость, минимум багов, есть в App Store и Google Play.
            </Typography.Text>
            <Space>
              <a href="https://apps.apple.com/app/happ-proxy-utility/id6504287215"
                 target="_blank" rel="noreferrer">
                <Tag color="blue">App Store</Tag>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.happproxy"
                 target="_blank" rel="noreferrer">
                <Tag color="green">Google Play</Tag>
              </a>
              <a href="https://www.happ.su/" target="_blank" rel="noreferrer">
                <Tag>happ.su</Tag>
              </a>
            </Space>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        message="Какой таб выбрать для каждого клиента"
        description={
          <Table
            size="small"
            pagination={false}
            showHeader={false}
            style={{ marginTop: 8, background: "transparent" }}
            dataSource={clientGuide}
            rowKey="client"
            columns={[
              {
                dataIndex: "client",
                title: "Клиент",
                render: (v: string, r: any) =>
                  r.best ? (
                    <Typography.Text strong>
                      <Badge status="success" /> {v}
                    </Typography.Text>
                  ) : (
                    v
                  ),
              },
              {
                dataIndex: "recommend",
                title: "Формат",
                render: (v: string) => <Tag color="blue">{v}</Tag>,
              },
            ]}
          />
        }
      />

      <Tabs
        defaultActiveKey="happ"
        items={formats.map((f) => ({
          key: f.key,
          label: f.recommended ? <b>{f.label}</b> : f.label,
          children: (
            <Card size="small">
              <Space direction="vertical" size="middle"
                     style={{ width: "100%" }} align="center">
                <div style={{
                  background: "#fff", padding: 12, borderRadius: 6,
                  border: f.recommended
                    ? "2px solid #52c41a"
                    : "1px solid #f0f0f0",
                }}>
                  <QRCodeSVG value={f.url} size={220} level="M" />
                </div>
                <Space wrap>
                  {f.clients.map((c) => (
                    <Tag key={c} color={f.recommended ? "green" : "blue"}>
                      {c}
                    </Tag>
                  ))}
                </Space>
                <Typography.Paragraph
                  copyable={{ text: f.url }}
                  style={{
                    fontFamily: "monospace", fontSize: 11,
                    padding: 8, background: "#f5f5f5",
                    borderRadius: 4, marginBottom: 0,
                    wordBreak: "break-all", width: "100%",
                  }}
                >
                  {f.url}
                </Typography.Paragraph>
                <Typography.Text type="secondary"
                  style={{ fontSize: 12, textAlign: "center" }}>
                  {f.description}
                </Typography.Text>
              </Space>
            </Card>
          ),
        }))}
      />
    </Space>
  );
};
